import { useEffect, useRef } from 'react';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useFoldersWithGuidelines, useKnowledgeBasesList } from '@/shared/hooks';
import {
  useChatId,
  useCurrentAgent,
  useCurrentAgentId,
  useInputOptions,
  useSetInputOptions,
} from '@/features/chats/stores';
import { useCampaignFiles } from '../../ui/ChatOptions/TemplatesAgent/useCampaignFiles';
import {
  getSessionSettings,
  getWorkspaceDefaultSettings,
  pickPersistableSettings,
  saveSettings,
  settingsEqual,
} from './chatSettingsStorage';
import { reconcileTemplatesSettings } from './reconcileTemplatesSettings';

/** Registry id of the templates agent (see ChatOptions.registry.ts). */
// eslint-disable-next-line i18next/no-literal-string -- agent registry id
const TEMPLATES_AGENT_ID = 'templates';

/**
 * Restores the templates-agent chat settings and keeps the in-memory store in sync with the persisted
 * snapshot — the fix for settings being wiped from the DISPLAY when a session is (re)opened. Templates
 * agent only; a no-op for every other agent.
 *
 * Persistence (store → storage) is owned by the user-interaction handlers via
 * {@link persistTemplatesSettingsFromStore}, so storage is authoritative. This hook does the reverse
 * (storage → store): whenever the store's persistable options diverge from the reconciled stored
 * snapshot, it re-asserts them. That way, after `setAgentById` resets `chatInput.options` and the option
 * components re-seed their DEFAULTS, the stored values are re-applied and the display is correct — no
 * matter how many times the reset+reseed happens.
 *
 * The model is included: we restore `llmOptions.model` AND mirror it to the top-level `model` field that
 * the legacy `ModelsDropdown` chip binds to (V2's chip reads `llmOptions.model` directly). Setting both
 * keeps the popup's `setOptions`→`toStore` round-trip consistent.
 *
 * Loop-safety: the sync writes only on a real diff (`settingsEqual`), so once the store matches storage it
 * is a no-op — no ping-pong (the default reseed only fires on EMPTY options, once). User edits update
 * storage synchronously before any re-render, so the sync reads a storage that already matches the store
 * and can never revert a fresh user change.
 */
export function useChatSettingsPersistence(): void {
  const agentId = useCurrentAgentId();
  const chatId = useChatId();
  const workspaceId = useCurrentWorkspaceId();
  const options = useInputOptions();
  const setInputOptions = useSetInputOptions();

  const isTemplates = agentId === TEMPLATES_AGENT_ID;

  // Reconciliation sources — only fetched for the templates agent.
  const kbQuery = useKnowledgeBasesList(undefined, isTemplates);
  const guidelinesQuery = useFoldersWithGuidelines(isTemplates);
  const selectedKbIds = (options?.knowledgeBaseIds as string[] | undefined) ?? [];
  const { campaignFiles, isLoading: campaignsLoading } = useCampaignFiles(
    isTemplates ? selectedKbIds : [],
  );
  // The model's allow-list is the agent's `enabledModels`. Kept as the store's own array (no `?? []`) so
  // its identity is stable across renders.
  const enabledModels = useCurrentAgent()?.enabledModels;

  // Latest options without making them an effect dependency where we don't want to re-arm.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const scope = { chatId: chatId || undefined, workspaceId: workspaceId || undefined };

  // --- Store ← storage sync: re-assert stored settings whenever the store diverges. ---
  useEffect(() => {
    if (!isTemplates || !workspaceId) return;

    // Source: a resumed session restores its own snapshot (incl. campaign); a brand-new chat uses the
    // per-workspace tooling default.
    const sessionStored = chatId ? getSessionSettings(chatId) : undefined;
    const stored = sessionStored ?? getWorkspaceDefaultSettings(workspaceId);
    if (!stored) return;

    // Prune stale ids once the lists have settled; before that, pass the stored values through so the
    // display is populated immediately (never left on the components' defaults).
    const queriesSettled = !kbQuery.isLoading && !guidelinesQuery.isLoading;
    const reconciled = queriesSettled
      ? reconcileTemplatesSettings(stored, {
          knowledgeBaseIds: kbQuery.isSuccess
            ? kbQuery.data.filter((kb) => kb.id && !kb.isInternal).map((kb) => kb.id)
            : undefined,
          guidelineIds: guidelinesQuery.isSuccess
            ? guidelinesQuery.data.flatMap((folder) => folder.guidelines.map((g) => g.id))
            : undefined,
          allowedModels: enabledModels && enabledModels.length > 0 ? enabledModels : undefined,
        })
      : stored;

    const storedPersistable = pickPersistableSettings(stored as Record<string, unknown>);
    const target = pickPersistableSettings(reconciled as Record<string, unknown>);
    const current = pickPersistableSettings(optionsRef.current);

    // Base merge is override (storage wins per key) while PRESERVING an in-memory value the user set
    // that isn't in storage yet — e.g. a campaign picked in a brand-new chat (it only lands in a
    // `session:` entry once the session exists, and is stripped from the `ws:` default by design), or a
    // component-default `model` when the stored snapshot carries none.
    //
    // But override-merge can only overwrite, never DELETE. `reconcile` signals "this whole field is
    // stale" (every KB/guideline id invalid, or the model no longer enabled) by OMITTING the key — and
    // a fully-stale field left in the store would be displayed, re-persisted, and sent to the backend.
    // So for a field that the stored snapshot HAD but reconcile removed, strip it from the base first so
    // the omission actually deletes it (the control then falls back to its default). A field simply
    // ABSENT from storage is left to the in-memory value — it wasn't pruned, so override-merge stands.
    const RECONCILE_OWNED = ['knowledgeBaseIds', 'selectedGuidelineIds', 'llmOptions'] as const;
    const prunedKeys = new Set<string>(
      RECONCILE_OWNED.filter(
        (key) => storedPersistable[key] !== undefined && target[key] === undefined,
      ),
    );
    const modelPruned = prunedKeys.has('llmOptions'); // top-level `model` mirrors `llmOptions.model`
    const base = Object.fromEntries(
      Object.entries(optionsRef.current ?? {}).filter(
        ([key]) => !prunedKeys.has(key) && !(modelPruned && key === 'model'),
      ),
    );
    // Mirror the restored model into the top-level `model` field the legacy chip reads (V2 reads
    // `llmOptions.model` directly). Only set it when the target carries a model, so an override that
    // omits the model (stored snapshot has none) doesn't wipe the in-memory one.
    const restoredModel = target.llmOptions?.model;
    const nextOptions = {
      ...base,
      ...target,
      ...(restoredModel !== undefined ? { model: restoredModel } : {}),
    };

    // No-op guard on the ACTUAL next persistable state (not an override-merge) so a needed strip still
    // runs. Terminates: after one write the store equals `nextOptions`, so the next run is a no-op.
    if (settingsEqual(pickPersistableSettings(nextOptions), current)) return;

    setInputOptions(nextOptions);
  }, [
    isTemplates,
    workspaceId,
    chatId,
    options,
    kbQuery.isLoading,
    kbQuery.isSuccess,
    kbQuery.data,
    guidelinesQuery.isLoading,
    guidelinesQuery.isSuccess,
    guidelinesQuery.data,
    enabledModels,
    setInputOptions,
  ]);

  // --- Campaign staleness: once files load for the (restored) KBs, drop a campaign that's gone. ---
  useEffect(() => {
    if (!isTemplates || campaignsLoading) return;
    const selected = optionsRef.current?.selectedCampaignId as string | undefined;
    if (!selected || campaignFiles.some((file) => file.fileId === selected)) return;

    const next = { ...(optionsRef.current ?? {}) };
    delete next.selectedCampaignId;
    setInputOptions(next);
    saveSettings(pickPersistableSettings(next), scope); // keep storage in sync with the drop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTemplates, campaignsLoading, campaignFiles, setInputOptions, chatId, workspaceId]);
}
